import React, { FC, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { generateFollowThroughPattern } from "@xapp/arachne-utils";
import {
    Handler,
    App as StentorApp,
    Entity as StentorEntity,
    Handler as StentorHandler,
    Intent as StentorIntent,
    APP_STATUS_INCOMPLETE,
} from "stentor-models";

import { INTELLIGENT_SEARCH } from "constants/channels";
import {
    CreateOrgAndAppOnboardingMutationVariables,
    UpdateAppInput,
    useCreateChannelsMutation,
    useCreateOrgAndAppOnboardingMutation,
    UserProfile,
    useStartCrawlMutation,
    useUpdateApplicationMutation,
    useUpdateStatusMutation,
} from "generated/graphql";
import { getApiUrl } from "utils/getApiUrl";
import { wrapUrqlMutation, convertHandler } from "utils/graphql";
import { useBoolState } from "utils/bool";
import { getProducts } from "utils/getProducts";
import { cleanObj } from "utils/Json";
import { ChatWidgetInput, generateCreateAppWidgetInput } from "utils/generateCreateAppWidgetInput";
import { FormWidgetInput, generateFormWidgetInput } from "utils/generateFormWidgetInput";

import { buildDefaultSearchTheme } from "components/SearchWidgetThemeBuilder/SearchWidgetThemeBuilder";
import { LoadingStep } from "components/Onboarding/components/V3";

import { OnboardingProps } from "../../models";

function useQueryParams() {
    return new URLSearchParams(useLocation().search);
}

export interface ConfigureAndCreateStepContainerProps {
    /**
     * Slows everything down.
     */
    debug?: boolean;
    /**
     * Profile of the user
     */
    profile: UserProfile;
    /**
     * Selected product
     */
    product: string;
    /**
     * Onboarding props
     */
    onboarding: OnboardingProps;
    /**
     * The user is ready for the next step
     *
     * @param app
     */
    onNextStep(props: OnboardingProps): void;
}

/**
 * V3 of Create and Configure Step
 * @param props
 * @returns
 */
export const ConfigureAndCreateStepContainer: FC<ConfigureAndCreateStepContainerProps> = (props) => {
    const { onNextStep, product: productId, profile, debug, onboarding } = props;

    const timeout = debug ? 5000 : 200;

    console.log("ConfigureAndCreateStepContainer");
    console.log(`Using timeout of ${timeout}`);

    const queryParams = useQueryParams();

    const [creationStatusMessage, setCreationStatusMessage] = useState<string>();
    const [creating, stopCreating, startCreating] = useBoolState(false);
    const [error, setError] = useState<{ error: string; message: string }>();

    const products = getProducts();
    const product = products.find((product) => product.id === productId);

    // TODO: LOAD EXECUTION ID!

    /**
     * Define some mutations we will use during creation.
     *
     * We want to suppress the global alerts and errors and instead display them on the page itself
     */
    const createApp = wrapUrqlMutation(useCreateOrgAndAppOnboardingMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean; message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the app for you." });
            return { send: false };
        },
    });

    const updateApplication = wrapUrqlMutation(useUpdateApplicationMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean; message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({
                error,
                message: "We're sorry, we had trouble updating the contact information on the app for you.",
            });
            return { send: false };
        },
    });

    const startCrawl = wrapUrqlMutation(useStartCrawlMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean; message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble starting the website crawl for you." });
            return { send: false };
        },
    });

    const updateStatus = wrapUrqlMutation(useUpdateStatusMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean; message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble updating the status for you." });
            return { send: false };
        },
    });

    const createChannels = wrapUrqlMutation(useCreateChannelsMutation(), {
        onSuccessMessage: false,
        onErrorMessage: (errors: string[]): { send: boolean; message?: string } => {
            // Concatenate the messages
            const error = errors.join(", ");
            setError({ error, message: "We're sorry, we had trouble creating the channels for you." });
            return { send: false };
        },
    });

    /**
     * The main method fo app creation, it all of the mutations.
     *
     * We will want to refactor this to make it more potable and reusable.
     */
    const handleCreate = useCallback(
        async (createProps: OnboardingProps) => {
            startCreating();
            createProps = cleanObj(createProps);

            console.log("Creating app with props");
            console.log(createProps);

            // Update the current fields in case of an error
            sessionStorage.setItem("onboarding-appProps", JSON.stringify(createProps));

            // load the template JSON
            const templateName = createProps.template?.name || "lead capture...";
            setCreationStatusMessage(`Preparing data for ${templateName}...`);

            // Old way...
            // const templateUrl = createProps?.template?.url || "https://templates.xapp.ai/contact-capture.json";
            const templateId = createProps?.template?.id || "contact-capture";
            const templateUrl = `/templates/${templateId}.json`;

            type Template = {
                app: StentorApp;
                entities: StentorEntity[];
                intents: StentorIntent[];
                handlers: StentorHandler[];
            };

            const template: Template = await fetch(templateUrl)
                .then((response) => {
                    return response.json();
                })
                .catch((e) => {
                    setError({ message: `Could not load the template: ${templateId}`, error: e.message });
                    stopCreating();
                });
            if (!template) {
                // bail
                return;
            }
            setCreationStatusMessage(`Template ${template.app.name} loaded...`);
            await new Promise((r) => setTimeout(r, timeout));
            /**
             * No model for our templates so we clean off utterances & don't pass intents & entities
             */
            const cleanHandler = (handler: Handler): Handler => {
                // delete slots
                const cleaned = { ...handler };
                cleaned.slots = [];
                cleaned.utterancePatterns = [];

                return cleaned;
            };

            const handlers = template.handlers.map(cleanHandler).map(convertHandler);

            const variables: CreateOrgAndAppOnboardingMutationVariables = {
                app: {
                    app: {
                        // TODO: What about no name?
                        name: createProps.name || "My Business",

                        website: createProps.website || undefined,
                        templateType: templateId,
                        //   icon: createProps.logo
                        // TODO: Ability to add leadsContact & leadsPhoneContact
                        // leadsContact: createProps.leadsContact || undefined,
                        // leadsContactPhone: createProps.leadsContactPhone || undefined,
                        businessDescription: createProps.businessDescription || undefined,
                    },
                    // they are empty so we don't create a model
                    entities: [],
                    intents: [],
                    handlers,
                },
                // When we have an existing org, we still need to pass the org param with the name
                // The backend should recognize the organizationId and add the app to the existing org
                org: {
                    // TODO: What about no name?
                    name: createProps.name || "My Business",
                    // logoUrl: createProps.logo
                    // When hasExistingOrg is true, the backend should use the organizationId
                    // passed separately to add to the existing org
                },
            };

            setCreationStatusMessage("Creating AI Agent...");
            await new Promise((r) => setTimeout(r, timeout));

            // If we have an organizationId, pass it to the mutation
            const mutationVariables = createProps.organizationId
                ? { ...variables, organizationId: createProps.organizationId }
                : variables;

            console.log("Creating app with variables:");
            console.log(mutationVariables);

            const result = await createApp(mutationVariables);

            const app = result?.data?.org?.add?.app;

            if (!app) {
                stopCreating();
                return;
            }

            // FIRST MAJOR STEP COMPLETE

            const { appId } = app;
            const organizationId = app.organizationId || createProps.organizationId;
            createProps.appId = appId;
            // Only update organizationId if we don't have one already
            if (!createProps.organizationId) {
                createProps.organizationId = organizationId;
            }

            const updateAppInput: UpdateAppInput = {};

            const partner: string | undefined = queryParams.get("partner") || product?.metadata?.["partner"];

            if (partner) {
                updateAppInput.refernceId = {
                    service: partner,
                    // we don't know this yet
                    id: "unknown",
                };
            }

            updateAppInput.beta = createProps.beta;

            // add place
            if (createProps.place) {
                updateAppInput.location = {
                    streetAddress: createProps.place.formattedAddress,
                };

                updateAppInput.places = [
                    {
                        name: createProps.place.name,
                        formattedAddress: createProps.place.formattedAddress,
                        addressComponents: createProps.place.addressComponents,
                        phone: createProps.place.phone,
                        placeId: createProps.place.placeId,
                        website: createProps.place.website,
                    },
                ];
            }

            if (createProps.leadsContact) {
                updateAppInput.leadsContact = createProps.leadsContact;
            }

            await new Promise((r) => setTimeout(r, timeout));

            await updateApplication({
                appId,
                app: updateAppInput,
            });

            if (app.website) {
                // Start Crawl
                setCreationStatusMessage("Starting website ingestion...");
                const pattern: string[] = [generateFollowThroughPattern(app.website)];
                await startCrawl({
                    appId,
                    url: app.website,
                    pattern,
                    channelId: "",
                    //    channelId:  widget.id
                });
            }

            // Create Chat Widget & Search Bar (& GBM);
            setCreationStatusMessage("Configuring channels...");
            await new Promise((r) => setTimeout(r, timeout));

            const chatName = createProps.name ? `${createProps.name}` : "Chat Widget";

            // START WIDGET CREATE
            const chatWidgetProps: ChatWidgetInput = {
                ...createProps,
                name: chatName,
                // TODO: See if we have a primary color and use that
                primaryColor: createProps?.primaryColor || "#1AA7DC",
                appId,
                templateId: createProps?.template?.id || "contact-capture",
            };

            const chat = generateCreateAppWidgetInput(chatWidgetProps);

            const apiUrl = getApiUrl();

            const search = {
                type: INTELLIGENT_SEARCH,
                name: "AI Search",
                theme: buildDefaultSearchTheme(createProps?.primaryColor || "#1AA7DC"),
                connection: {
                    serverUrl: `${apiUrl}/apps/${appId}/run`,
                },
                autocompleteSuggestionsUrl: `${apiUrl}/chat/suggestions?key=$CHANNEL_KEY&type=FAQ`,
            };

            const formName = createProps.name ? `${createProps.name} Booking` : "Booking";

            // Create Schedular!
            const appFormInput: FormWidgetInput = {
                ...createProps,
                name: formName,
                appId,
                primaryColor: createProps?.primaryColor || "#1AA7DC",
            };

            if (createProps.place && createProps.place.formattedAddress) {
                appFormInput.address = {
                    streetAddress: createProps.place?.formattedAddress,
                };
            }

            const form = generateFormWidgetInput(appFormInput);

            const channelResult = await createChannels({
                appId,
                chat,
                search,
                form,
                lex: {
                    type: "lex-v2",
                    botName: templateId,
                },
            });

            // get all the IDs
            const chatWidgetId = channelResult?.data?.app.update.channel.addChatWidgetChannel.id;
            const formWidgetId = channelResult?.data?.app.update.channel.addFormWidgetChannel.id;
            const searchWidgetId = channelResult?.data?.app.update.channel.addIntelligentSearchChannel.id;
            const lexChannelId = channelResult?.data?.app.update.channel.addLexV2Channel.id;

            createProps.chatWidgetId = chatWidgetId;
            createProps.formWidgetId = formWidgetId;
            createProps.searchWidgetId = searchWidgetId;
            createProps.lexChannelId = lexChannelId;

            await updateStatus({
                appId,
                type: APP_STATUS_INCOMPLETE,
                notes: `Created ${new Date().toDateString()} by ${profile.email} during onboarding.`,
            });

            // Done
            setCreationStatusMessage(`Great start! Preparing next step...`);
            await new Promise((r) => setTimeout(r, timeout));

            // Move on!
            onNextStep(createProps);
        },
        [
            createApp,
            createChannels,
            onNextStep,
            product?.metadata,
            profile?.email,
            queryParams,
            startCrawl,
            startCreating,
            stopCreating,
            timeout,
            updateApplication,
            updateStatus,
        ]
    );

    useEffect(() => {
        // if we already have an appId, just go to the next step
        if (onboarding.appId) {
            onNextStep(onboarding);
            return;
        }

        // Not creating, we have a profile and we don't have an appId already
        if (!creating && profile && !onboarding.appId) {
            handleCreate(onboarding);
        }
    }, [creating, handleCreate, onNextStep, onboarding, profile, startCreating, stopCreating]);

    return (
        <>
            <LoadingStep loadingMessage={creationStatusMessage} errorMessage={error?.message} />
        </>
    );
};
